import Card from "@/design-system/Card/Card";
import { Collapsible } from "@/design-system/Collapsible/Collapsible";
import { InfoTip } from "@/design-system/InfoTip/InfoTip";
import Input from "@/design-system/Input/Input";
import { TextButton } from "@/design-system/TextButton/TextButton";
import { CoinIcon } from "@/designSystemIcons";
import { cn } from "@/lib/utils";
import type { GalleryPlanInput } from "@repo/shared/api";
import {
  type GalleryPlanLimit,
  compact,
  describeGalleryPlan,
  formatAmountInput,
  grouped,
} from "@repo/shared/gallery";
import {
  type SavedGalleryPlan,
  useGalleryPlanFields,
} from "@repo/shared/hooks";
import { useTranslation } from "@repo/shared/i18n";
import {
  ArrowLeftRight,
  ChevronDown,
  Layers,
  SlidersHorizontal,
  TriangleAlert,
} from "lucide-react";
import { ReactNode, useId } from "react";

import {
  GalleryTokenIcon,
  VERDICT_COLORS,
  Verdict,
  VerdictInfo,
} from "./GallerySetCard";

const PLAN_GRID =
  "grid grid-cols-2 gap-2.5 mobile:grid-cols-3 touch-device:grid-cols-6";

export type GalleryPlannerProps = {
  plan?: SavedGalleryPlan;
  /** The plan's sets and tokens completed since it was built. */
  done?: { tokens: number; sets: number };
  /** The category a build plans; none for All. */
  categoryId?: number;
  tokensPlaceholder: string;
  budgetPlaceholder: string;
  setsPlaceholder: string;
  building: boolean;
  /** The last build found nothing within the limits: 0 tokens and a warning. */
  empty: boolean;
  /** The failed build's message, the only line shown while set. */
  error?: string;
  onBuild: (input: GalleryPlanInput) => void;
  onClear: () => void;
  open: boolean;
  /** Shows "Fine-tune" on the folded planner once solutions are built. */
  highlight: boolean;
  onToggle: () => void;
};

/**
 * The Gallery Planner: one meter tile per limit. Any change re-solves live, an
 * empty field sets no limit and all fields empty means no plan.
 */
export const GalleryPlanner = ({
  plan,
  done,
  categoryId,
  tokensPlaceholder,
  budgetPlaceholder,
  setsPlaceholder,
  building,
  empty,
  error,
  onBuild,
  onClear,
  open,
  highlight,
  onToggle,
}: GalleryPlannerProps) => {
  const { t } = useTranslation("gallery");
  const tokensId = useId();
  const { fields, setField } = useGalleryPlanFields({
    plan,
    categoryId,
    onBuild,
    onClear,
  });
  const {
    verdict,
    nothingToBuy,
    tokenWarning: warning,
  } = describeGalleryPlan(plan, empty);
  // On the Tokens result: why the plan falls short of the target.
  const tokenWarning =
    warning &&
    (warning.key === "plan.empty"
      ? t("plan.empty")
      : t("plan.maxTokens", { tokens: warning.tokens }));

  const tiles: {
    key: GalleryPlanLimit;
    icon: ReactNode;
    placeholder: string;
    result?: number;
    /** Completed of the result since the build, shown as "done / result". */
    done?: number;
    format: (n: number) => string;
  }[] = [
    {
      key: "targetTokens",
      icon: <GalleryTokenIcon className="size-4" />,
      placeholder: tokensPlaceholder,
      result: empty ? 0 : plan?.totals.tokens,
      done: done?.tokens,
      format: grouped.format,
    },
    {
      key: "budget",
      icon: <CoinIcon size={14} />,
      placeholder: budgetPlaceholder,
      result: plan?.totals.coinsNeeded,
      format: compact.format,
    },
    {
      key: "maxTaxLoss",
      icon: <CoinIcon size={14} />,
      placeholder: t("plan.noLimit"),
      result: plan?.totals.taxLoss,
      format: compact.format,
    },
    {
      key: "maxTransfers",
      icon: <ArrowLeftRight className="size-3.5" />,
      placeholder: t("plan.noLimit"),
      result: plan?.totals.transfers,
      format: grouped.format,
    },
    {
      key: "maxSets",
      icon: <Layers className="size-3.5" />,
      placeholder: setsPlaceholder,
      result: plan?.totals.sets,
      done: done?.sets,
      format: grouped.format,
    },
  ];

  return (
    <Card hoverable={false} className="flex flex-col">
      {/* The whole header toggles, so the padding sits on the button. */}
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="group/planner flex min-w-0 items-center gap-3 rounded-[inherit] p-4 text-left"
      >
        <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-primary-action/15 text-blue-200">
          <SlidersHorizontal className="size-5" />
        </span>
        <span className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="text-body-lg text-neutral-100">
            {t("plan.title")}
          </span>
          <span className="text-body text-neutral-400 max-mobile:truncate">
            {t("plan.description")}
          </span>
        </span>
        {/* Beside the caret, centred on the whole header. A folded planner still filters the grid, so it says so. */}
        {!open && (plan || highlight) && (
          <span className="whitespace-nowrap text-sm font-semibold text-blue-200 group-hover/planner:text-blue-100">
            {plan ? t("plan.active") : t("plan.fineTune")}
          </span>
        )}
        <span className="text-blue-200 group-hover/planner:text-blue-100">
          <ChevronDown
            strokeWidth={2.5}
            className={cn(
              "size-4 transition-transform duration-200",
              open && "rotate-180",
            )}
          />
        </span>
      </button>
      <Collapsible open={open}>
        <div className="flex flex-col gap-3.5 px-4 pb-4">
          {/* The same columns as the plan row, so each limit sits over its result. */}
          <div className={PLAN_GRID}>
            {tiles.map((tile) => (
              <div key={tile.key} className="flex min-w-0 flex-col gap-1.5">
                <span className="flex min-h-5 items-center gap-1">
                  <label
                    htmlFor={tile.key === "targetTokens" ? tokensId : undefined}
                    className="truncate text-xs font-semibold uppercase text-neutral-400"
                  >
                    {t(`plan.limits.${tile.key}`)}
                  </label>
                  {tile.key === "targetTokens" && (
                    <InfoTip label={t("plan.limits.targetTokens")}>
                      {t("plan.tokenTargetInfo")}
                    </InfoTip>
                  )}
                </span>
                <Input
                  id={tile.key === "targetTokens" ? tokensId : undefined}
                  aria-label={t(`plan.limits.${tile.key}`)}
                  size="small"
                  inputMode="decimal"
                  icon={tile.icon}
                  placeholder={tile.placeholder}
                  value={fields[tile.key]}
                  onChange={(v) => setField(tile.key, formatAmountInput(v))}
                />
              </div>
            ))}
          </div>
          <div className="flex flex-col gap-2">
            <h3 className="text-small font-semibold text-neutral-300">
              {t("plan.resultTitle")}
            </h3>
            <div className={PLAN_GRID}>
              {tiles.map((tile) => (
                <div
                  key={tile.key}
                  className="flex min-w-0 flex-col gap-1 rounded-xl bg-neutral-750 px-3 py-2.5"
                >
                  <span className="truncate text-small text-neutral-400">
                    {t(`plan.results.${tile.key}`)}
                  </span>
                  <span className="flex items-center gap-1.5 whitespace-nowrap tabular-nums">
                    {tile.icon}
                    <strong
                      className={cn(
                        "text-h3",
                        tile.result === undefined
                          ? "text-neutral-500"
                          : "text-neutral-50",
                      )}
                    >
                      <span>
                        {tile.result === undefined
                          ? "–"
                          : tile.format(tile.done || tile.result)}
                      </span>
                      {tile.result !== undefined && !!tile.done && (
                        <span className="text-body text-neutral-400">
                          {` / ${tile.format(tile.result)}`}
                        </span>
                      )}
                    </strong>
                    {tile.key === "targetTokens" && tokenWarning && (
                      <InfoTip
                        label={tokenWarning}
                        width={280}
                        icon={<TriangleAlert className="size-4" />}
                        className="text-amber-400 hover:text-amber-300"
                      >
                        {tokenWarning}
                      </InfoTip>
                    )}
                  </span>
                </div>
              ))}
              {/* The whole plan's verdict: its tax loss per token gained, like a set card's. */}
              <InfoTip
                className="h-full w-full min-w-0 flex-col gap-1 rounded-xl bg-neutral-750 px-3 py-2.5 text-left text-current hover:text-current"
                width={300}
                icon={
                  <>
                    <span className="truncate text-small text-neutral-400">
                      {t("list.verdict")}
                    </span>
                    <span className="flex min-h-[27px] items-center gap-1.5">
                      {(nothingToBuy || verdict) && (
                        <span
                          className={cn(
                            "whitespace-nowrap font-oswald text-sm font-medium uppercase tracking-wide",
                            nothingToBuy
                              ? "text-s-tier-foreground"
                              : verdict && VERDICT_COLORS[verdict],
                          )}
                        >
                          {nothingToBuy
                            ? t("verdict.super")
                            : t(`verdict.${verdict}`)}
                        </span>
                      )}
                      <Verdict
                        verdict={verdict}
                        nothingToBuy={nothingToBuy}
                        className="size-5"
                      />
                    </span>
                  </>
                }
              >
                <VerdictInfo />
              </InfoTip>
            </div>
          </div>
          <div className="flex flex-col items-start gap-2 text-body text-neutral-400">
            {/* One line at most: an error, then a build going, then the empty-fields hint. */}
            {error ? (
              <p className="text-amber-400">{error}</p>
            ) : building ? (
              <p>{t("plan.building")}</p>
            ) : (
              !plan && !empty && <p>{t("plan.none")}</p>
            )}
            {!building && (plan || empty) && (
              <TextButton onClick={onClear} className="text-neutral-300">
                {t("plan.clear")}
              </TextButton>
            )}
          </div>
        </div>
      </Collapsible>
    </Card>
  );
};
